import { motion } from "framer-motion";
import { Check, ArrowUpRight, Sparkles } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import ContactForm from "@/components/site/ContactForm";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PRICING, FAQS } from "@/lib/site-data";

export default function Pricing() {
  return (
    <main data-testid="pricing-page" className="bg-ab-bg text-ab-text">
      <Navbar />

      <section className="relative pt-36 md:pt-44 pb-16 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-b from-ab-bg via-[#EFEDE6] to-ab-bg" />
          <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[820px] h-[420px] rounded-full bg-ab-accent/10 blur-3xl" />
        </div>
        <div className="ab-container text-center max-w-4xl mx-auto">
          <p className="ab-eyebrow text-ab-accent">Pricing · transparent by design</p>
          <h1 className="ab-h1 text-6xl md:text-8xl mt-5">
            Pay for what your hospital <span className="italic font-light text-ab-accent">actually</span> uses.
          </h1>
          <p className="mt-8 text-lg text-ab-muted leading-relaxed">
            Start with one module, scale to the full OS. No per-bed surprise fees. No "enterprise unlocks" hidden behind a sales call you don't need.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="ab-container grid grid-cols-1 md:grid-cols-3 gap-6">
          {PRICING.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              data-testid={`pricing-card-${p.id}`}
              className={`relative rounded-3xl p-8 md:p-10 flex flex-col ${
                p.featured
                  ? "bg-ab-primary text-white border border-ab-primary shadow-[0_30px_80px_-30px_rgba(13,64,53,0.45)]"
                  : "bg-white border border-ab-border"
              }`}
            >
              {p.featured && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-ab-accent text-white text-xs">
                  <Sparkles className="w-3 h-3" /> Most loved
                </span>
              )}
              <p className={`ab-eyebrow ${p.featured ? "text-white/60" : "text-ab-muted"}`}>{p.tagline}</p>
              <h3 className={`mt-3 font-serif text-4xl md:text-5xl leading-tight ${p.featured ? "text-white" : "text-ab-primary"}`}>{p.name}</h3>

              <div className="mt-8 flex items-baseline gap-2">
                <span className={`font-serif text-5xl md:text-6xl ${p.featured ? "text-white" : "text-ab-primary"}`}>{p.price}</span>
                {p.period && <span className={`text-sm ${p.featured ? "text-white/70" : "text-ab-muted"}`}>{p.period}</span>}
              </div>

              <ul className={`mt-8 space-y-3 text-sm ${p.featured ? "text-white/85" : "text-ab-text/85"}`}>
                {p.features.map((f, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${p.featured ? "text-ab-accent" : "text-ab-accent"}`} />
                    {f}
                  </li>
                ))}
              </ul>

              <a
                href="#contact"
                data-testid={`pricing-cta-${p.id}`}
                className={`mt-10 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full transition group ${
                  p.featured ? "bg-white text-ab-primary hover:bg-ab-accent hover:text-white" : "ab-pill-btn"
                }`}
              >
                {p.cta}
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </motion.div>
          ))}
        </div>

        <div className="ab-container mt-16 text-center text-sm text-ab-muted">
          All plans include implementation support, ABDM rails and free upgrades. Annual billing saves 18%.
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-ab-bg py-24 md:py-32 border-t border-ab-border">
        <div className="ab-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="ab-eyebrow text-ab-accent">FAQ</p>
            <h2 className="ab-h2 text-5xl md:text-6xl mt-4">
              Quick answers, <span className="italic font-light">honest</span> ones.
            </h2>
            <p className="mt-6 text-ab-muted max-w-md">
              Everything else, our founder will personally answer in your demo call.
            </p>
          </div>
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="w-full" data-testid="pricing-faq">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-ab-border">
                  <AccordionTrigger data-testid={`faq-trigger-${i}`} className="font-serif text-xl md:text-2xl text-ab-primary hover:no-underline py-6">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-ab-muted leading-relaxed pb-6 text-base">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <ContactForm />
      <Footer />
    </main>
  );
}
